import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from "@angular/forms";
import { Router } from "@angular/router";
import { Subscription, BehaviorSubject, Observable, of, forkJoin } from "rxjs";
import { map, tap } from "rxjs/operators";
import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { CanDeactivateComponent } from "../../../common/classes/can-deactivate.component";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { IssueType, Issue, IssuesStatus } from "../../../common/models/issue.model";
import { ComplianceReportingType, WellSelectionMode } from "../../models/compliance.model";
import { IssuesService } from "../../../common/services/issues.service";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { OrganizationService } from "../../../common/services/organization.service";
import { OrganizationStub } from "../../../common/models/organization.model";
import { AgGridBooleanFilterComponent } from "../../../common/components/ag-grid/filters/boolean-filter.component";
import { CheckboxRendererComponent } from "../../../common/components/ag-grid/checkbox-renderer.component";
import { WellService } from "../../../common/services/well.service";
import { WellStub, GISPoint, GISPointTypes } from "../../../common/models/well.model";
import { RouterLinkRendererComponent } from "../../../common/components/ag-grid/router-link-renderer.component";
import { LoaderService } from "../../../common/services/loader.service";
import { AuthService } from "../../../services/auth.service";

@Component({
    selector: 'app-create-issue',
    templateUrl: './create-issue.component.html',
    styleUrls: ['./create-issue.component.scss'],
    standalone: false
})
export class CreateIssueComponent extends CanDeactivateComponent implements OnInit, OnDestroy {

    applicableTypes: string[] = ["AD", "ADLD", "ADS", "BDS", "F8", "F8A", "F8B", "F8C", "F8F", "LOGD", "INS", "FS", "FA", "F13", "IFEE", "PDUD", "PDUE", "PDUF", "C2AR-ERP", "C2AR-SWIW", "SMQR", "SMSR"];

    uicTypes: string[] = [ "AD", "ADLD", "C2AR-ERP", "C2AR-SWIW", "F13", "IFEE", "SMQR", "SMSR" ];
    permittingTypes: string[] = [ "ADS", "BDS", "F8", "F8A", "F8B", "F8C", "F8F", "LOGD" ];
    productionTypes: string[] = [ "PDUD", "PDUE", "PDUF" ];
    suretyTypes: string[] = [ "FA", "FS", "INS" ];
    confirmDeactivate: Dialog = new Dialog("You have unsubmitted changes.  Are you sure you wish to leave the page?", [new DialogButton("Cancel", false, () => { return false; }), new DialogButton("OK", true, () => { this.deactivateConfirmed(); })]);
    issueCreatedDialog: Dialog = new Dialog("Issue has been successfully created and will need to be validated via the Issues View", [new DialogButton("OK", true, () => { this.complete(); })]);
    form: FormGroup;
    formErrors: string[] = [];

    complianceTypes: ComplianceReportingType[];
    issueTypes: IssueType[];

    organization: OrganizationStub;
    organizationData: Observable<OrganizationStub[]>;


    organizationColumns: ColDef[] = [
        {
            headerClass: "wrapped",
            headerCheckboxSelection: false,
            checkboxSelection: true,
            maxWidth: 50
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Name",
            headerTooltip: "Name",
            field: "name",
            tooltipField: "name",
            minWidth: 150,
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "RICS Owner #",
            headerTooltip: "RICS Owner #",
            field: "id",
            tooltipField: "id",
            maxWidth: 150,
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "RBDMS Owner #",
            headerTooltip: "RBDMS Owner #",
            field: "historicalOwnerNumber",
            tooltipField: "historicalOwnerNumber",
            maxWidth: 150,
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Has Registered?",
            headerTooltip: "Has Registered?",
            field: "hasRegistered",
            cellRenderer: CheckboxRendererComponent,
            cellRendererParams: {
                action: () => { return false; },
                field: "hasRegistered",
                description: "Has Registered?",
                isVisible: () => { return true; },
                isEnabled: () => { return false; },
                renderAsYesNo: true
            },
            tooltipValueGetter: this.formatter.formatBooleanToYesNo("hasRegistered"),
            filter: AgGridBooleanFilterComponent,
            filterParams: this.formatter.defaultBooleanFilterParams,
            maxWidth: 150
        }
    ];

	organizationGridOptions: GridOptions;
	organizationGridApi?: GridApi;
    organizationSearchHelp: string = "If none of the organizations match your search, recheck your information, reset the form, and run the search again.";

    organizationSearchRun: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    searchingOrganizations: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    
    get selectedIssueType(): IssueType {
        if (this.form == null) return null;
        const typeCtrl = this.form.get("issueType");
        if (typeCtrl == null) return null;
        if (this.issueTypes == null) return null;
        return this.issueTypes.find(t => t.id == typeCtrl.value);
    }

    get relatedComplianceType(): ComplianceReportingType {
        if (this.selectedIssueType == null) return null;
        if (this.productionTypes.find(p => p === this.selectedIssueType.id) != null) return this.complianceTypes.find(c => c.typeId === "PREP");
        return this.complianceTypes.find(c => c.typeId === this.selectedIssueType.id);
    }

    get isAnnual(): boolean {
        if (this.relatedComplianceType == null) return false;
        if (this.relatedComplianceType.typeId === 'PREP') return true;
        return this.relatedComplianceType.isAnnual;
    }

    get isQuarterly(): boolean {
        if (this.relatedComplianceType == null) return false;
        if (this.selectedIssueType.id === 'PDUF') return true;
        return this.relatedComplianceType.isQuarterly;
    }

    quarters: string[] = ["Q1", "Q2", "Q3", "Q4"];


    selectedWell: WellStub;

    subscription: Subscription = new Subscription();
    typesLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    wellColumns: ColDef[] = [
        {
            headerClass: "wrapped",
            headerCheckboxSelection: false,
            checkboxSelection: true,
            maxWidth: 50
        } as ColDef,
        {
            cellRenderer: RouterLinkRendererComponent,
            cellRendererParams: {
                openInNewWindow: true,
                textField: "id",
                url: (data: WellStub) => { return `/common/organization/${data.organizationId}/well/${data.id}` }
            },
            checkboxSelection: false,
            headerName: "API Number",
            headerTooltip: "API Number",
            field: "id",
            tooltipField: "id",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 130
        } as ColDef,
        {
            headerName: "Well Name",
            headerTooltip: "Well Name",
            field: "lease.name",
            tooltipField: "lease.name",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150
        } as ColDef,
        {
            headerName: "Well Number",
            headerTooltip: "Well Number",
            field: "wellNumber",
            tooltipField: "wellNumber",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 140
        } as ColDef,
        {
            headerName: "County",
            headerTooltip: "County",
            field: "location.gisPoints",
            tooltipValueGetter: this.formatter.arraySelectorFormatter("location.gisPoints", this.getCountyName),
            valueFormatter: this.formatter.arraySelectorFormatter("location.gisPoints", this.getCountyName),
            valueGetter: this.formatter.arraySelectorFormatter("location.gisPoints", this.getCountyName),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 135
        } as ColDef,
        {
            headerName: "Township",
            headerTooltip: "Township",
            field: "location.gisPoints",
            tooltipValueGetter: this.formatter.arraySelectorFormatter("location.gisPoints", this.getTownshipName),
            valueFormatter: this.formatter.arraySelectorFormatter("location.gisPoints", this.getTownshipName),
            valueGetter: this.formatter.arraySelectorFormatter("location.gisPoints", this.getTownshipName),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150
        } as ColDef,
        {
            headerName: "Slant",
            headerTooltip: "Slant",
            field: "location.slant",
            tooltipField: "location.slant",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150
        } as ColDef      
    ];

    wellGridOptions: GridOptions;
	wellGridApi?: GridApi;

    wellData: Observable<WellStub[]>;

    get wellRequired(): boolean {
        if (this.relatedComplianceType == null) return false;
        if (this.relatedComplianceType.typeId === 'PREP') return true;
        return this.relatedComplianceType.apiNumbers !== WellSelectionMode.None;
    }

    wellSearchRun: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(dialogService: DynamicDialogService, private router: Router, private builder: FormBuilder, private issuesService: IssuesService, private headerService: SubHeaderService,
        private organizationService: OrganizationService, private formatter: GridFormatterService, private wellService: WellService, private loader: LoaderService, auth: AuthService)
    {
        super(dialogService, auth);
    }

    ngOnInit() {
        this.form = this.buildForm();
        this.defineGrids();
        this.headerService.updateSubHeader({ title: "Create New Issue" });
        const issueObs$ = this.issuesService.getAllIssueTypes().pipe(
            map((types: IssueType[]) => {                
                return types.filter(t => this.applicableTypes.findIndex(a => a === t.id) !== -1);
            })
        );
        this.typesLoading.next(true);
        const complianceObs$ = this.issuesService.getComplianceReportingTypes();
        const typeSubs$ = forkJoin(issueObs$, complianceObs$).pipe(tap(([issues, compliances]) => {
            issues = issues.filter(i => compliances.find(c => c.typeId === i.id && c.isEnabled === true) != null || this.productionTypes.find(p => i.id === p) != null || this.suretyTypes.find(s => i.id === s) != null);
            this.issueTypes = issues;
            this.complianceTypes = compliances;
        })).subscribe(
            () => {
                this.typesLoading.next(false);
            },
            (error) => {
                this.typesLoading.next(false);
                this.handleError(error);
            }
        );
        this.subscription.add(typeSubs$);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.subscription) this.subscription.unsubscribe();
    }

    buildForm(): FormGroup {
        return this.builder.group({
            ricsNumber: ["", []],
            rbdmsNumber: ["", []],
            ownerName: ["", []],
            organizationFound: [false, [Validators.requiredTrue]],
            issueType: ["", [Validators.required]],
            dueDate: [null, [Validators.required]],
            apiNumber: ["", []],
            reportingYear: [null, []],
            reportingQuarter: ["", []]
        });
    }

    cancel(): void {
        this.router.navigate(["/home"]);
    }

    complete(): void {
        this.allowDeactivate.next(true);
        this.router.navigate(["/home"]);
    }

    create(): void {
        if (this.form.valid) {
            const issue = {
                organizationId: this.organization.id,
                organizationNameAtTimeOfIssue: this.organization.name,
                legacyId: this.organization.historicalOwnerNumber,
                description: this.selectedIssueType.description,
                typeId: this.selectedIssueType.id,
                dateDue: this.form.get("dueDate").value,
                status: IssuesStatus.Pending,
                reportingYear: this.form.get("reportingYear").value,
                reportingQuarter: this.form.get("reportingQuarter").value
            } as Issue;
            if (this.selectedWell != null) {
                issue.wellId = this.selectedWell.id;
                issue.wellNameAtTimeOfIssue = (this.selectedWell.lease != null) ? this.selectedWell.lease.name : this.selectedWell.name;
            }
            if (this.productionTypes.findIndex(p => p === this.selectedIssueType.id) !== -1) {
                issue.issueOwner = 'Production';
            } else if (this.uicTypes.findIndex(p => p === this.selectedIssueType.id) !== -1) {
                issue.issueOwner = 'UIC';
            } else if (this.permittingTypes.findIndex(p => p === this.selectedIssueType.id) !== -1) {
                issue.issueOwner = 'Permitting';
            } else if (this.suretyTypes.findIndex(p => p === this.selectedIssueType.id) !== -1) {
                issue.issueOwner = 'Surety';
            }

            this.loader.toggleLoader(true);
            const createSubs$ = this.issuesService.createIssue(issue).pipe(                
            ).subscribe(
                (issue: Issue) => {                        
                    this.loader.toggleLoader(false);
                    this.issueCreatedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.issueCreatedDialog);
                },
                (error) => { this.handleError(error); }
            );
            this.subscription.add(createSubs$);
        }

    }

    deactivateConfirmed(): void {
        this.allowDeactivate.next(true);
    }

    defineGrids(): void {
        this.organizationGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.organizationColumns, false, true, true, true);
		this.organizationGridOptions.onGridReady = (params) => {
			this.organizationGridApi = params.api;
			this.organizationGridApi.sizeColumnsToFit();
		}
        this.wellGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.wellColumns, false, true, true, true);
		this.wellGridOptions.onGridReady = (params) => {
			this.wellGridApi = params.api;
			this.wellGridApi.sizeColumnsToFit();
		};
    }

    dueDateChanged(date: Date): void {
        const dateCtrl = this.form.get("dueDate");
        if (dateCtrl != null) {
            dateCtrl.setValue(date);
            dateCtrl.updateValueAndValidity();
        }
    }

    getCountyName(array: GISPoint[]) {
        let pt = array.find(x => x.pointType === GISPointTypes.WellHead);
        return pt && pt.county ? pt.county.name : '';
    }

    getTownshipName(array: GISPoint[]) {
        let pt = array.find(x => x.pointType === GISPointTypes.WellHead);
        return pt && pt.township ? pt.township.name : '';
    }

    handleError(error: any): void {
        console.error(error);
        this.loader.toggleLoader(false);
        this.dialogService.showError();
    }

    issueTypeChanged(evt: Event): void {        
        if (this.relatedComplianceType != null) {
            const apiCtrl = this.form.get("apiNumber");
            if (apiCtrl != null) {
                if (this.wellRequired) {
                    apiCtrl.setValidators([Validators.required]);
                } else {
                    apiCtrl.clearValidators();
                }
                apiCtrl.updateValueAndValidity();
            }
            const yearCtrl = this.form.get("reportingYear");
            if (yearCtrl != null) {
                if (this.isAnnual) {
                    yearCtrl.setValidators([Validators.required, Validators.min(2000), Validators.max(2100)]);
                } else {
                    yearCtrl.clearValidators();
                }
                yearCtrl.updateValueAndValidity();
            }
            const qtrCtrl = this.form.get("reportingQuarter");
            if (qtrCtrl != null) {
                if (this.isQuarterly) {
                    qtrCtrl.setValidators([Validators.required]);
                } else {
                    qtrCtrl.clearValidators();
                }
            }
        }
    }

    onOrganizationGridSelecting(evt: Event): void {
        const rows = this.organizationGridApi.getSelectedRows();
        if (rows && rows.length > 0) {
            this.organization = rows[0] as OrganizationStub; //should ever only be one selected row as its in single selection mode
        } else {
            this.organization = null;
        }
        const foundCtrl = this.form.get("organizationFound");
        if (foundCtrl != null) {
            foundCtrl.setValue(this.organization != null);
            foundCtrl.updateValueAndValidity();
            this.allowDeactivate.next(false);
        }
    }

    onWellGridSelecting(evt: Event): void {
        const rows = this.wellGridApi.getSelectedRows();
        if (rows && rows.length > 0) {
            this.selectedWell = rows[0] as WellStub; //should ever only be one selected row as its in single selection mode
        } else {
            this.selectedWell = null;
        }
        const apiCtrl = this.form.get("apiNumber");
        if (apiCtrl != null) {
            let id = (this.selectedWell != null) ? this.selectedWell.id : null;
            apiCtrl.setValue(id);
            apiCtrl.updateValueAndValidity();
        }
    }

    reset(): void {
        this.form.reset();
        this.organization = null;
        this.organizationSearchRun.next(false);
        this.organizationData = null;
        this.formErrors = [];
        this.allowDeactivate.next(this.selectedIssueType == null && this.selectedWell == null); 
    }

    searchOrganization(option: string): void {
        this.formErrors = [];
        let obs$: Observable<OrganizationStub[]> = of([]);
        let ctrl: AbstractControl;
        switch (option) {
            case "rics":
                ctrl = this.form.get("ricsNumber");
                if (ctrl != null) {
                    if (ctrl.value.toString().startsWith("ON")) {
                        obs$ = this.organizationService.getOrganizationStub(ctrl.value).pipe(
                            map((stub: OrganizationStub) => {
                                this.searchingOrganizations.next(false);
                                return [stub];
                            })
                        );
                    } else {
                        this.formErrors.push("RICS Owner #'s must start with 'ON'.");
                    }                   
                }
                break;
            case "rbdms":
                ctrl = this.form.get("rbdmsNumber");
                if (ctrl != null) {
                    obs$ = this.organizationService.getOrganizationStubByHistoricalOwnerNumber(ctrl.value).pipe(
                        map((stub: OrganizationStub) => {
                            this.searchingOrganizations.next(false);
                            return [stub];
                        })
                    );
                }
                break;
            case "name":
                ctrl = this.form.get("ownerName");
                if (ctrl != null) {
                    obs$ = this.organizationService.getOrganizationStubsByPartialName(ctrl.value).pipe(
                        tap((data: OrganizationStub[]) => {
                            this.searchingOrganizations.next(false);
                        })
                    );
                }
                break;
        }
        this.organizationSearchRun.next(this.formErrors.length === 0);
        if (this.organizationGridApi) this.organizationGridApi.sizeColumnsToFit();
        this.organizationData = obs$;
    }

    searchWells(): void {
        this.formErrors = [];
        if (this.organization != null) {
            this.wellSearchRun.next(true);
            this.wellData = this.wellService.getWellsStubsByOrganizationId(this.organization.id);
        } else {
            this.formErrors.push("You must first select an organization before searching for wells.");
        }
    }
}
